import { Button, useTheme } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { TUpdateOneLinkChunk } from '../../../contexts/OneLinkSummaryProvider';
import { select2FARecipientsList } from '../../../store/selectors/bucket';
import { IOneLinkWithCampaignSummaries } from '../../../types/oneLink';
import { TwoFASidebarToggle } from '../../index';
import { ConfirmationDialog } from '../../_mui';
import { Section } from './common';

interface IProps {
  reportMode?: boolean;
  instance: IOneLinkWithCampaignSummaries | null | undefined;
  onEdit?: () => void;
  onSubmit: (chunk: TUpdateOneLinkChunk) => void;
}

export const TwoFASection: React.FC<IProps> = ({ reportMode, onSubmit, instance, onEdit }) => {
  const twoFARecipientList = useSelector(select2FARecipientsList);
  const theme = useTheme();

  const [dialogOpen, setDialogOpen] = useState(false);

  const isAllowedDomainsConfigured = useMemo(
    () => Boolean(instance?.allowed_domains && instance?.allowed_domains.length > 0),
    [instance?.allowed_domains],
  );

  const is2FAEnabled = useMemo(() => instance?.enabled_2fa, [instance?.enabled_2fa]);

  const handleOpen2FASidebar = useCallback(() => {
    if (isAllowedDomainsConfigured) {
      return setDialogOpen(true);
    }

    if (!is2FAEnabled) {
      onSubmit({
        security_info: {
          enabled_2fa: true,
          allowed_domains: [],
          max_submit_count: instance?.max_submit_count ?? 0,
          password: instance?.password ?? '',
        },
      });

      setTimeout(() => {
        onEdit?.();
      }, 500);
    }

    onEdit?.();
  }, [isAllowedDomainsConfigured, is2FAEnabled, onSubmit, onEdit, instance?.max_submit_count, instance?.password]);

  const handleConfirm = useCallback(async () => {
    setDialogOpen(false);

    try {
      await onSubmit({
        security_info: {
          enabled_2fa: true,
          allowed_domains: [],
          max_submit_count: instance?.max_submit_count ?? 0,
          password: instance?.password ?? '',
        },
      });

      setTimeout(() => {
        onEdit?.();
      }, 1000);
    } catch (error) {
      console.error('2FA activation failed:', error);
    }
  }, [instance?.password, instance?.max_submit_count, onSubmit, onEdit]);

  const total2FARecipients = reportMode ? instance?.recipients_count : twoFARecipientList?.successfulRecipients?.length;

  const buttonTitle = useMemo(() => {
    switch (true) {
      case !is2FAEnabled:
        return 'Enable 2FA';
      case is2FAEnabled && !total2FARecipients:
        return 'Manage recipients';
      case is2FAEnabled && total2FARecipients && total2FARecipients > 0:
        return `Manage ${total2FARecipients} recipient${total2FARecipients === 1 ? '' : 's'}`;
      default:
        return 'Enable 2FA';
    }
  }, [is2FAEnabled, total2FARecipients]);

  return (
    <Section title="Two-factor Authentication (2FA)">
      <Button
        title={buttonTitle}
        onClick={handleOpen2FASidebar}
        variant="contained"
        size="large"
        fullWidth
        sx={{ color: theme.palette.common.white }}
      >
        {buttonTitle}
      </Button>
      <TwoFASidebarToggle
        count={total2FARecipients ?? 0}
        oneLinkId={instance?.uid}
        status={instance?.status}
        hideControl={true}
      />
      <ConfirmationDialog
        open={dialogOpen}
        title="Confirm configure 2FA"
        message="If you want to configure 2FA, then Domain protection will be disabled."
        confirmButtonText="Configure 2FA"
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
      />
    </Section>
  );
};
